import { useState } from 'react'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import type { Connector, ISODate } from '@app/shared'

import {
  useDeleteMailRule,
  useDisconnectMailAccount,
  useMailAccounts,
  useMailRules,
  useStartGoogleMailConnect,
  useSyncConnector,
} from '../../lib/data'
import type { MailAccount, MailRule } from '../../lib/data/types'
import { PILE_WORD } from '../day/designed'
import { Envelope } from './icons'
import { isInAppPath, MAX_MAIL_ACCOUNTS, mailResultText, type MailResult } from './mail'
import { accountSyncLine } from './model'
import { ActionButton, StatusPill } from './parts'

// ============================================================================
// GMAIL — the one connector the owner connects from this screen.
//
// Every other card here reports on a connection someone set up on the Worker.
// This one IS the setup: Google sign-in, up to three accounts, read-only. So it
// carries what the others do not — the list of connected addresses, a real
// `Connect` button, and a per-account `Disconnect` that asks first, inline, in
// the card (the ThreadList pattern: a sentence and two buttons, never a
// browser confirm()).
//
// THE MAIL SYNC (Lane 1b) reads every account every 30 minutes; `Sync now`
// runs it on demand. Each account row says its own "last sync · N messages",
// or nothing about syncing when it has never synced (absent, not zero), and
// its own error in a sentence — one broken inbox is named on its own row.
// ============================================================================

const NOTE_STRIP: Record<string, string> = {
  error: 'bg-danger-fill text-danger font-medium',
  warning: 'bg-warning-fill text-warning font-medium',
  neutral: 'bg-surface-2 text-ink-3 font-normal',
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

export function GmailCard({
  connector,
  mailResult,
  today,
  nowMinutes,
}: {
  connector: Connector
  /** The `?mail=` outcome the page read off the URL once, or null. */
  mailResult?: MailResult | null
  /** The clock the per-account "last sync" ages are measured against. */
  today: ISODate
  nowMinutes: number
}) {
  const accounts = useMailAccounts()
  const start = useStartGoogleMailConnect()
  const sync = useSyncConnector()
  const navigate = useNavigate()

  const list = accounts.data ?? []
  const full = list.length >= MAX_MAIL_ACCOUNTS
  const broken = connector.status === 'error'

  const connect = () =>
    start.mutate(undefined, {
      onSuccess: (url) => {
        if (isInAppPath(url)) navigate(url, { replace: true })
        else window.location.assign(url)
      },
    })

  return (
    <article
      className={clsx(
        'flex flex-col gap-3 rounded-card border bg-surface px-[17px] py-[15px]',
        broken ? 'border-danger-line' : 'border-line',
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex size-[38px] flex-none items-center justify-center rounded-row border border-line bg-surface-2"
          aria-hidden
        >
          <Envelope size={19} strokeWidth={1.8} className="text-ink-3" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
          <div className="flex items-center gap-2">
            <h3 className="text-[13.5px] font-semibold leading-none">{connector.name}</h3>
            {connector.status === 'ok' && <StatusPill tone="success" label="OK" />}
            {broken && <StatusPill tone="danger" label="ERROR" />}
            {connector.status === 'disconnected' && <StatusPill tone="neutral" label="NOT CONNECTED" />}
          </div>
          <p className="text-[11px] font-normal leading-[1.4] text-microcopy text-pretty">
            {connector.scope}
          </p>
        </div>
      </div>

      {connector.note && (
        <p
          className={clsx(
            'rounded-tile px-3 py-[10px] text-[11.5px] leading-[1.4] text-pretty',
            NOTE_STRIP[connector.note.tone],
          )}
        >
          {connector.note.text}
        </p>
      )}

      {list.length > 0 && (
        <ul className="flex flex-col divide-y divide-line rounded-tile border border-line">
          {list.map((account) => (
            <AccountRow key={account.id} account={account} today={today} nowMinutes={nowMinutes} />
          ))}
        </ul>
      )}

      {accounts.error && (
        <p className="text-[11px] leading-[1.4] text-danger text-pretty">
          {errorText(accounts.error, 'The connected accounts could not be read.')}
        </p>
      )}

      {list.length > 0 && <SenderRules />}

      {/* Only once the list has answered: a label chosen off a list still loading would say
          "Connect a Google account" to an owner who already has two. */}
      {accounts.data && (
        <div className="flex items-center gap-3">
          {/* Only with an account to read: a sync of nothing would be a button that does nothing. */}
          {list.length > 0 && (
            <ActionButton
              label={sync.isPending ? 'Syncing…' : 'Sync now'}
              variant="outline"
              busy={sync.isPending}
              onClick={() => sync.mutate(connector.id)}
            />
          )}
          {full ? (
            <p className="text-[11px] leading-[1.4] text-microcopy">
              Three accounts connected — the most LifeOS reads.
            </p>
          ) : (
            <ActionButton
              label={
                start.isPending
                  ? 'Opening Google…'
                  : list.length === 0
                    ? 'Connect a Google account'
                    : 'Connect another account'
              }
              variant={list.length === 0 ? 'dark' : 'outline'}
              busy={start.isPending}
              onClick={connect}
            />
          )}
        </div>
      )}

      {/* The click that just happened, in words: a refused start (keys not set) or the result the
          callback sent back. Below the action, like the sync error on the other cards. */}
      {start.error && (
        <p className="text-[11px] leading-[1.4] text-danger text-pretty">
          {errorText(start.error, 'Google sign-in could not be started.')}
        </p>
      )}
      {/* A sync that could not even be asked for. A sync that RAN and failed for one account
          says so on that account's row instead, once the list re-reads. */}
      {sync.error && (
        <p className="text-[11px] leading-[1.4] text-danger text-pretty">
          {errorText(sync.error, 'That sync could not be started, and the reason did not come back with a sentence attached.')}
        </p>
      )}
      {mailResult && !start.error && (
        <p
          role="status"
          className={clsx(
            'text-[11px] leading-[1.4] text-pretty',
            mailResult.kind === 'error' ? 'text-danger' : 'text-success',
          )}
        >
          {mailResultText(mailResult)}
        </p>
      )}
    </article>
  )
}

/**
 * One connected address. `Disconnect` asks first, in place: the address, what happens, and two
 * buttons. A credential is gone after the second click, so the first click only asks.
 */
function AccountRow({ account, today, nowMinutes }: { account: MailAccount; today: ISODate; nowMinutes: number }) {
  const [confirming, setConfirming] = useState(false)
  const disconnect = useDisconnectMailAccount()
  const syncLine = accountSyncLine(account, today, nowMinutes)

  return (
    <li className="flex flex-col gap-[7px] px-3 py-[9px]">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-[11.5px] font-medium leading-none text-ink-2">
          {account.address}
        </span>
        {account.status === 'error' && <StatusPill tone="danger" label="ERROR" />}
        {!confirming && (
          <ActionButton label="Disconnect" variant="quiet" size="xs" onClick={() => setConfirming(true)} />
        )}
      </div>

      {syncLine && <p className="text-[10.5px] leading-[1.4] text-microcopy">{syncLine}</p>}

      {account.lastError && (
        <p className="text-[10.5px] leading-[1.4] text-danger text-pretty">{account.lastError}</p>
      )}

      {confirming && (
        <div className="flex flex-col gap-[7px]">
          <p className="text-[10.5px] leading-[1.4] text-danger-2 text-pretty">
            Disconnect {account.address}? LifeOS forgets its sign-in and asks Google to revoke it.
          </p>
          <div className="flex items-center gap-[10px]">
            <button
              type="button"
              disabled={disconnect.isPending}
              onClick={() => disconnect.mutate(account.id)}
              className="rounded-context border border-danger-line bg-danger-fill px-[10px] py-[5px] text-[11px] font-semibold leading-none text-danger-2"
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </button>
            <button
              type="button"
              onClick={() => {
                disconnect.reset()
                setConfirming(false)
              }}
              className="text-[11px] font-medium leading-none text-ink-3 hover:text-ink"
            >
              Keep it
            </button>
          </div>
          {disconnect.error && (
            <p className="text-[10.5px] leading-[1.4] text-danger text-pretty">
              {errorText(disconnect.error, 'That account could not be disconnected. Try again.')}
            </p>
          )}
        </div>
      )}
    </li>
  )
}

/**
 * THE SENDER RULES — "always important / always noise from …", made on the Day page (the ⋯ menu
 * and the weekly check) and managed here. Not designed (day-page README → Not designed), so it is
 * kept in this card's own voice: plain rows, one quiet Delete each. Deleting a rule changes nothing
 * in Gmail; the messages it filed are re-sorted by Claude on the next run.
 */
function SenderRules() {
  const rules = useMailRules()
  const remove = useDeleteMailRule()
  if (rules.error) {
    return (
      <p className="text-[11px] leading-[1.4] text-danger text-pretty">
        {errorText(rules.error, 'The sender rules could not be read.')}
      </p>
    )
  }
  const rows = rules.data ?? []
  if (rows.length === 0) return null
  return (
    <div className="flex flex-col gap-[6px]">
      <p className="text-[11px] font-semibold leading-none text-ink-3">Sender rules</p>
      {/* Rows are divs with list roles, not <li>: the card's <li>s are its ACCOUNTS, and the account
          list is what the card's own tests (and a screen reader's "list, 2 items") count. */}
      <div role="list" aria-label="Sender rules" className="flex flex-col divide-y divide-line rounded-tile border border-line">
        {rows.map((rule) => (
          <div role="listitem" key={rule.id} className="flex items-center gap-2 px-3 py-[9px]">
            <span className="min-w-0 flex-1 truncate text-[11.5px] leading-[1.4] text-ink-2">{ruleLine(rule)}</span>
            <ActionButton
              label={remove.isPending && remove.variables === rule.id ? 'Deleting…' : 'Delete'}
              variant="quiet"
              size="xs"
              busy={remove.isPending && remove.variables === rule.id}
              onClick={() => remove.mutate(rule.id)}
            />
          </div>
        ))}
      </div>
      {remove.error && (
        <p className="text-[10.5px] leading-[1.4] text-danger text-pretty">
          {errorText(remove.error, 'That rule could not be deleted. Try again.')}
        </p>
      )}
    </div>
  )
}

/** 'always important from kelsey@x.com · 3 open' — the open count only when the Worker sends it. */
export function ruleLine(rule: MailRule): string {
  const base = `always ${PILE_WORD[rule.verdict]} from ${rule.matchValue}`
  return rule.messageCountOpen === undefined ? base : `${base} · ${rule.messageCountOpen} open`
}
